import { apiRequest } from "@/lib/api-client";

import type { Clip, ClipEditPlan, ClipReviewReason, ClipReviewStatus } from "./types";
import type { PublishingJob, PublishingVisibility } from "@/features/publishing/types";

export function listClips(sourceId?: string) {
  const query = sourceId ? `?source_id=${encodeURIComponent(sourceId)}` : "";
  return apiRequest<Clip[]>(`/clips${query}`);
}

export function updateClipReviewStatus(
  clipId: string,
  reviewStatus: ClipReviewStatus,
  reviewReason: ClipReviewReason | null = null,
  reviewNotes: string | null = null
) {
  return apiRequest<Clip>(`/clips/${clipId}/review`, {
    method: "PATCH",
    body: JSON.stringify({
      review_status: reviewStatus,
      review_reason: reviewReason,
      review_notes: reviewNotes
    })
  });
}

export function preferClip(clipId: string) {
  return apiRequest<Clip>(`/clips/${clipId}/prefer`, { method: "POST" });
}

export function rerenderClip(clipId: string) {
  return apiRequest<Clip>(`/clips/${clipId}/rerender`, {
    method: "POST"
  });
}

export function renderMissingClipVariants(momentId: string) {
  return apiRequest<Clip[]>(`/clips/moments/${momentId}/missing-variants`, {
    method: "POST"
  });
}

export function updateClipEditPlan(clipId: string, plan: ClipEditPlan) {
  return apiRequest<Clip>(`/clips/${clipId}/edit-plan`, {
    method: "PATCH",
    body: JSON.stringify({
      title_text: plan.title.text,
      title_enabled: plan.title.enabled,
      title_start_seconds: plan.title.start_seconds,
      title_end_seconds: plan.title.end_seconds,
      captions_enabled: plan.captions.enabled,
      crop_mode: plan.crop.mode
    })
  });
}

export function initializeClipEditPlan(clipId: string) {
  return apiRequest<Clip>(`/clips/${clipId}/edit-plan`, {
    method: "POST"
  });
}

export function deleteClip(clipId: string) {
  return apiRequest<null>(`/clips/${clipId}`, { method: "DELETE" });
}

export function publishClip(
  clipId: string,
  platform = "dry_run",
  accountId: string | null = null,
  scheduledAt: string | null = null,
  // null means "use the target account's default_visibility" — a one-off override for
  // this publish only, which never changes the account itself.
  visibility: PublishingVisibility | null = null
) {
  return apiRequest<PublishingJob>(`/clips/${clipId}/publish`, {
    method: "POST",
    body: JSON.stringify({
      platform,
      account_id: accountId,
      scheduled_at: scheduledAt,
      visibility
    })
  });
}

export function publishClipToGroup(clipId: string, groupId: string, scheduledAt: string | null = null) {
  return apiRequest<PublishingJob[]>(`/clips/${clipId}/publish-group`, {
    method: "POST",
    body: JSON.stringify({
      group_id: groupId,
      scheduled_at: scheduledAt
    })
  });
}
